import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	FormattedText,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	RelativeTimestamp,
	Row,
	StyleProps,
	TruncatedText,
	useSync,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { StudentNote, StudentNoteNoteType, truncateDisplayItems, useDynamicTruncation } from "../../../../Utilities";
import { StudentNoteCreateModalController, StudentNoteCreateModalControllerProps } from "../CreateModal";
import { StudentNoteIcon, studentNoteTypeColors } from "../Icon";
import { ControlledStudentNoteModal } from "../Modal";
import styles from "./styles.module.css";

export type PinnedStudentNotesRowProps = {
	isLoading?: boolean;
	studentNotes: PinnedStudentNoteStudentNote[] | null | undefined;
} & Omit<PinnedStudentNoteCreateButtonProps, "variant" | "size" | "className" | "style"> &
	Partial<ClassNameProps & StyleProps>;

export function PinnedStudentNotesRow({
	isLoading,
	studentNotes,
	className,
	style,
	...props
}: PinnedStudentNotesRowProps) {
	const ref = React.useRef<HTMLDivElement | null>(null);
	const { synchronize } = useSync();
	React.useEffect(() => {
		synchronize();
	}, [synchronize, studentNotes]);
	const gapSize = 12;
	const { maxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize },
	);
	const { displayedItems, anyHidden } = truncateDisplayItems(studentNotes ?? [], maxDisplayed, { includeLast: true });

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Row justify="spaced-start" className={className} style={style}>
			<PinnedStudentNoteCreateButton {...props} />

			<Row justify="spaced-start" align="center" style={{ width: "100%", minWidth: 0 }}>
				<div ref={ref} style={{ width: "100%", minWidth: 0 }}>
					<Row justify="spaced-start" horizontalSpacing={`${gapSize}px`} style={{ width: "100%" }}>
						{displayedItems.map((studentNode) => (
							<PinnedStudentNote
								key={studentNode.id}
								studentNote={studentNode}
								className={styles["pinned-student-notes-row__pinned-note"]}
							/>
						))}
					</Row>
				</div>

				<If condition={anyHidden}>
					<Button variant="link" onClick={openModal} title="View all pinned notes">
						<Icon.CaretRight size={32} className="block" />
					</Button>
				</If>
			</Row>

			<Modal.If condition={isModalOpen}>
				<PinnedStudentNotesListModal isLoading={isLoading} studentNotes={studentNotes} onClose={closeModal} />
			</Modal.If>
		</Row>
	);
}

type PinnedStudentNotesListModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> & {
	isLoading?: boolean;
	studentNotes: PinnedStudentNoteStudentNote[] | null | undefined;
};

function PinnedStudentNotesListModal({
	studentNotes,
	ifRef,
	onClose,
	className,
	style,
}: PinnedStudentNotesListModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={{ width: "50rem", height: "40rem", ...style }}>
			<Modal.Header>
				<Heading.H2 noMargin>Pinned Notes</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Grid tracks={3} gap="0.75rem">
					{studentNotes?.map((studentNode) => (
						<PinnedStudentNote key={studentNode.id} studentNote={studentNode} style={{ width: "100%" }} />
					))}
				</Grid>
			</Modal.Body>
		</Modal>
	);
}

export type PinnedStudentNoteStudentNote = Pick<StudentNote, "id" | "noteType" | "createdAt" | "body">;

export type PinnedStudentNoteProps = {
	studentNote: PinnedStudentNoteStudentNote;
} & Partial<ClassNameProps & StyleProps>;

export function PinnedStudentNote({ studentNote, className, style }: PinnedStudentNoteProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(styles["pinned-student-note"], className)}
				style={
					{
						"--note-color":
							studentNoteTypeColors[
								studentNote.noteType === StudentNoteNoteType.SocialEmotional ? "socialEmotional" : studentNote.noteType
							],
						...style,
					} as React.CSSProperties
				}
			>
				<Column justify="spaced-start" verticalSpacing="0.375rem">
					<TruncatedText lines={3} className={styles["pinned-student-note__body"]}>
						<FormattedText>{studentNote.body}</FormattedText>
					</TruncatedText>

					<Row justify="space-between">
						<RelativeTimestamp value={studentNote.createdAt} className="timestamp" />

						<StudentNoteIcon studentNote={studentNote} size={14} backgroundColor="transparent" style={{ padding: 0 }} />
					</Row>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<ControlledStudentNoteModal
					studentNoteID={studentNote.id}
					noteType={studentNote.noteType}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

export type PinnedStudentNoteCreateButtonProps = Omit<StudentNoteCreateModalControllerProps, "children">;

export function PinnedStudentNoteCreateButton({ className, ...props }: PinnedStudentNoteCreateButtonProps) {
	return (
		<StudentNoteCreateModalController
			title="Add a note"
			className={makeClassName(styles["pinned-student-note-create-button"], className)}
			{...props}
		>
			<Icon.FilePlus className={styles["pinned-student-note-create-button__icon"]} />
		</StudentNoteCreateModalController>
	);
}
